import { Icon } from '@blueprintjs/core'
import _ from 'lodash'
import React from 'react'

import { FramesManager } from 'shared-libs/components/view/frames-manager'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { default as SheetPortal } from 'browser/components/atomic-elements/atoms/sheet/sheet-portal'

/**
 * @uiComponent
 *
 * A summary trigger that opens the caller's `children` uiSchema in a slide-in sheet for editing a large
 * or nested value. Both the summary and the sheet form are described entirely in JSON through the normal
 * renderer (same `value`/frame binding as anywhere else); edits write through immediately (no separate
 * save — "Done" just closes, and the surrounding panel/detail owns persistence).
 *
 * The collapsed summary is the caller's `preview` slot — any uiSchema node, rendered in a child frame so
 * its value paths resolve in this component's frame. This component only wires the open handler onto it
 * (forwarded via the renderer's defaultProps, same mechanism as `ui:conditional`), so the whole trigger
 * — card chrome, text, edit icon — is pure JSON. Typically a `ui:view` styled with the existing
 * `c-entity-preview-card` classes containing a couple of bound-text lines and a `ui:icon`. If `preview`
 * is omitted, the trigger falls back to a bare edit icon.
 *
 * Example:
 * {
 *   "type": "ui:embeddedSheet",
 *   "sheetTitle": "Entity Spec",
 *   "preview": {
 *     "type": "ui:view",
 *     "className": "c-entity-preview-card c-entity-preview-card--isClickable",
 *     "style": { "flexDirection": "row", "alignItems": "center", "justifyContent": "space-between" },
 *     "children": [
 *       { "type": "ui:view", "children": [
 *         { "type": "ui:view", "className": "c-entity-preview-card--title", "mapStateToProps": { "text": "mixins" } },
 *         { "type": "ui:view", "style": { "opacity": 0.6 }, "mapStateToProps": { "text": "ingestionFlags.writeBehavior" } }
 *       ] },
 *       { "type": "ui:icon", "name": "edit" }
 *     ]
 *   },
 *   "children": [ ... the value's form uiSchema ... ]
 * }
 */
export interface IEmbeddedSheetProps extends IBaseProps {
  frames?: FramesManager
  /** Bound value (set the node's `value`, e.g. `"."`); drives the empty-state `placeholder`. */
  value?: any
  /** Sheet header text (distinct from the collapsed summary). */
  sheetTitle?: string
  /** Sheet size passed through to the underlying Sheet (e.g. "sm", "md"). */
  size?: string
  /** Trigger text shown in place of `preview` when the bound `value` is `undefined` (an unfilled entry). */
  placeholder?: string
}

interface IEmbeddedSheetState {
  isOpen: boolean
}

export class EmbeddedSheet extends React.Component<IEmbeddedSheetProps, IEmbeddedSheetState> {
  public state: IEmbeddedSheetState = { isOpen: false }

  private open = () => this.setState({ isOpen: true })
  private close = () => this.setState({ isOpen: false })

  public render() {
    const { children, className, style, sheetTitle, size } = this.props
    return (
      <div className={className} style={style}>
        {this.renderTrigger()}
        <SheetPortal isOpen={this.state.isOpen} size={size || 'sm'} footer={this.renderFooter()}>
          <div className="c-sheet-body--padded">
            {sheetTitle && <h4 className="c-sheet-title u-bumperBottom">{sheetTitle}</h4>}
            {children}
          </div>
        </SheetPortal>
      </div>
    )
  }

  /**
   * Render the caller's `preview` uiSchema in a child frame and forward `onClick: open` via the
   * renderer's defaultProps (same mechanism as ui:conditional), so the summary itself is the trigger.
   * Falls back to a bare edit icon when no `preview` slot is supplied.
   */
  private renderTrigger() {
    const { frames, placeholder, value } = this.props
    const preview = frames?.getContext('uiSchema')?.preview
    // Show the placeholder (still opens the sheet on click) when the bound value is undefined.
    if (placeholder && _.isUndefined(value)) {
      return (
        <div
          className="c-entity-preview-card c-entity-preview-card--isClickable"
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
          onClick={this.open}
        >
          <span className="c-placeholderColor">{placeholder}</span>
          <Icon icon="edit" />
        </div>
      )
    }
    if (preview && frames) {
      const renderer = frames.getContext('renderer')
      const uiSchemaPath = frames.getContext('uiSchemaPath').concat(['preview'])
      const previewFrames = renderer.createChildFrame(frames, { uiSchema: preview, uiSchemaPath })
      return renderer.createElementFromFrame(previewFrames, { onClick: this.open })
    }
    return <Icon icon="edit" onClick={this.open} />
  }

  private renderFooter() {
    // Only a "Done" button: edits write through live (no draft), so a "Cancel" that can't revert
    // would mislead. Omitting onCancelButtonClick hides the cancel button (see Footer.renderCancelButton).
    return <Footer isVisible={true} primaryButtonText="Done" onPrimaryButtonClick={this.close} />
  }
}
